import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Download, FolderOpen, Loader2, CheckCircle, SkipForward, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

function CSVImportModal({ isOpen, onClose, onImportComplete, theme }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showFormat, setShowFormat] = useState(false);

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
    setImportResults(null);
  };

  const handleImport = async () => {
    if (!selectedFile) { toast.error('Please select a CSV file'); return; }
    setImporting(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await axios.post(`${API_URL}/api/songs/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResults(response.data);
      const { imported, reactivated = 0, skipped, errors: errCount } = response.data;
      const total = imported + reactivated;
      if (total > 0) {
        const parts = [];
        if (imported > 0) parts.push(`${imported} new`);
        if (reactivated > 0) parts.push(`${reactivated} restored`);
        toast.success(`${parts.join(', ')} song${total !== 1 ? 's' : ''} added!`);
      } else if (skipped > 0 && errCount === 0) {
        toast(`All songs already in your library`);
      } else if (errCount > 0 && total === 0) {
        toast.error('No songs imported — check your CSV format matches the template');
      }
      if (total > 0 && skipped > 0) toast(`Skipped ${skipped} duplicates`);
      if (errCount > 0 && total > 0) toast.error(`${errCount} rows had errors`);
      await onImportComplete(response.data.songs || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = `title,artist,genre,duration\nSweet Child O' Mine,Guns N' Roses,Rock,5:56\nBohemian Rhapsody,Queen,Rock,5:55\nHey Jude,The Beatles,Rock,7:11`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'song-import-template.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResults(null);
    setShowFormat(false);
    onClose();
  };

  if (!isOpen) return null;

  const labelStyle = { fontWeight: 600, fontSize: '0.85rem', color: theme.text };

  return (
    <>
      <div onClick={handleClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200 }} />
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: theme.surface, borderRadius: '16px 16px 0 0',
        zIndex: 201, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
      }}>
        <div style={{ width: 36, height: 4, background: theme.border, borderRadius: 2, margin: '12px auto 0' }} />

        {/* Header */}
        <div style={{ padding: '1rem 1.5rem 0.75rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: theme.text }}>Import from CSV</div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '1.5rem', lineHeight: 1 }}>×</button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Format guide toggle */}
          <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
            <button
              onClick={() => setShowFormat(v => !v)}
              style={{
                width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.65rem 1rem', background: theme.surfaceAlt, border: 'none',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <span style={{ ...labelStyle, fontWeight: 600 }}>CSV format guide</span>
              {showFormat ? <ChevronUp size={16} color={theme.textMuted} /> : <ChevronDown size={16} color={theme.textMuted} />}
            </button>
            {showFormat && (
              <div style={{ padding: '0.75rem 1rem', borderTop: `1px solid ${theme.border}` }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: theme.textSecondary }}>
                  Required columns: <strong style={{ color: theme.text }}>title</strong>, <strong style={{ color: theme.text }}>artist</strong>. Optional: <strong style={{ color: theme.text }}>genre</strong>, <strong style={{ color: theme.text }}>duration</strong> (MM:SS). Exportify exports are supported automatically.
                </p>
                <pre style={{
                  margin: 0, fontSize: '0.78rem', color: theme.text,
                  background: theme.surface, padding: '0.6rem 0.75rem',
                  borderRadius: 6, border: `1px solid ${theme.border}`,
                  overflowX: 'auto',
                }}>
{`title,artist,genre,duration
Sweet Child O' Mine,Guns N' Roses,Rock,5:56
Bohemian Rhapsody,Queen,Rock,5:55`}
                </pre>
              </div>
            )}
          </div>

          {/* Exportify hint */}
          <div style={{ fontSize: '0.82rem', color: theme.textSecondary, background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '0.65rem 0.9rem' }}>
            Importing from Spotify? Export your playlist to CSV for free at{' '}
            <a href="https://exportify.net" target="_blank" rel="noreferrer" style={{ color: '#1DB954', fontWeight: 600 }}>exportify.net</a>
            {' '}— sign in with Spotify, click Export.
          </div>

          {/* Template download */}
          <button
            onClick={downloadTemplate}
            style={{
              width: '100%', padding: '0.7rem', background: 'none',
              border: `1px solid ${theme.border}`, borderRadius: 8,
              color: theme.textSecondary, fontSize: '0.9rem', fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.5rem', fontFamily: 'inherit',
            }}
          >
            <Download size={15} /> Download template CSV
          </button>

          {/* File picker */}
          <div style={{
            border: `2px dashed ${theme.border}`, borderRadius: 8,
            padding: '1.5rem 1rem', textAlign: 'center', background: theme.surfaceAlt,
          }}>
            <input type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} id="csv-file-input" />
            <label
              htmlFor="csv-file-input"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '0.65rem 1.5rem', background: theme.primary,
                color: theme.onPrimary, borderRadius: 8,
                cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
              }}
            >
              <FolderOpen size={15} /> Browse for CSV
            </label>
            {selectedFile && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: theme.textSecondary }}>
                {selectedFile.name}
              </div>
            )}
          </div>

          {/* Import button */}
          <button
            onClick={handleImport}
            disabled={!selectedFile || importing}
            style={{
              width: '100%', padding: '0.85rem',
              background: selectedFile && !importing ? theme.navGradient : theme.border,
              color: selectedFile && !importing ? theme.onPrimary : theme.textMuted,
              border: 'none', borderRadius: 8, fontSize: '0.95rem', fontWeight: 700,
              cursor: selectedFile && !importing ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.4rem', fontFamily: 'inherit',
            }}
          >
            {importing
              ? <><Loader2 size={15} /> Importing…</>
              : <><CheckCircle size={15} /> Import songs</>}
          </button>

          {/* Results */}
          {importResults && (
            <div style={{ background: theme.surfaceAlt, padding: '1rem', borderRadius: 8, border: `1px solid ${theme.border}` }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text, marginBottom: '0.5rem' }}>Import results</div>
              <div style={{ fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.text }}>
                  <CheckCircle size={14} color="#28a745" /> {importResults.imported} imported
                </div>
                {importResults.skipped > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.textSecondary }}>
                    <SkipForward size={14} /> {importResults.skipped} duplicates skipped
                  </div>
                )}
                {importResults.errors > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc3545' }}>
                    <XCircle size={14} /> {importResults.errors} errors
                  </div>
                )}
              </div>
              {importResults.errorDetails?.length > 0 && (
                <details style={{ marginTop: '0.6rem' }}>
                  <summary style={{ cursor: 'pointer', color: '#dc3545', fontWeight: 600, fontSize: '0.82rem' }}>
                    View errors ({importResults.errorDetails.length})
                  </summary>
                  <div style={{ marginTop: '0.4rem', maxHeight: 120, overflowY: 'auto', fontSize: '0.8rem', color: theme.textSecondary }}>
                    {importResults.errorDetails.map((err, i) => (
                      <div key={i} style={{ padding: '0.2rem 0', borderBottom: `1px solid ${theme.border}` }}>
                        Row {err.row}: {err.error}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CSVImportModal;
